import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Col, Row } from 'reactstrap';
import dayjs from 'dayjs';

import { AuthTags } from '~constants/authTags';
import { getTP1Urls } from '~constants/routes-tp1';

import { isBeforeDay } from '~libs/dayjs';

import { InputProps } from '~components/form-fields';
import * as FormInput from '~components/form-fields/inputs';

import { getAll as getCareWorkers } from '~care-workers/actions';

import { selectCurrentAppointment } from '~appointments/selectors/appointments';
import { selectLookupCareWorkers } from '~care-workers/selectors/users';
import { selectClientByID } from '~main/selectors/clients/clients';
import { selectUserTags } from '~main/selectors/login';

interface Params {
  filter_by?: { client_id: string; preference: string };
  or?: any[];
  care_worker?: boolean;
}

const FormInputCareWorkers: React.FC<InputProps> = ({
  id,
  value = {},
  disabled,
  onChange,
  manualReload = true,
  initialValue,
  selectorOptions,
}) => {
  const [filterValue, setFilterValue] = useState('all');
  const {
    client_id = '',
    date = dayjs().add(1, 'day'),
    user_id = initialValue?.value,
    id: appointmentId,
    repeat,
    support_coordination,
    case_management,
  } = value;
  const dispatch = useAppDispatch();
  const client = useAppSelector((state) => selectClientByID(state, parseInt(client_id)));
  const appointment = useAppSelector((state) => selectCurrentAppointment(state));
  const tags = useAppSelector(selectUserTags);
  const userLookup = selectorOptions ?? selectLookupCareWorkers;

  const authorised = tags.includes(AuthTags.APPOINTMENTS.UPDATE);
  const canUseVolunteer = tags.includes(AuthTags.USERS.VOLUNTEERS.VIEW);
  //none of the links we want use the user, settings or data so we pass empty to reduce loading requirements
  const {
    CAREWORKERS: { CareWorkerDetails },
  } = getTP1Urls({}, {}, {});
  const url = `${CareWorkerDetails}${user_id}`;

  const { preferences } = client ?? {};
  const { gender = null, yes = [], no = [] } = preferences ?? {};

  const isNewAppointment = !appointmentId;

  const isReadOnly = (!isNewAppointment && !repeat && isBeforeDay(dayjs(date), dayjs())) || disabled;

  const options = [
    { id: 'all', name: 'All Workers' },
    { id: 'preferred', name: 'Preferred Workers' },
    { id: 'historical', name: 'Historical Workers' },
  ];

  const selectorParams = useMemo(() => {
    if (case_management || support_coordination) return {};

    const params: Params = {
      filter_by: { client_id, preference: filterValue },
    };

    if (canUseVolunteer) params.or = [{ care_worker: true }, { department_id: 1 }];
    else params.care_worker = true;

    return params;
  }, [client_id, filterValue, canUseVolunteer, case_management, support_coordination]);

  let description = '';
  let descriptionColor = 'info';
  if (no?.includes(parseInt(user_id))) {
    description = 'Client does not prefer this worker';
    descriptionColor = 'danger';
  } else if (yes?.includes(parseInt(user_id))) {
    description = 'Client prefers this worker';
    descriptionColor = 'success';
  } else if (gender && gender > 0) {
    let genderPreference = '';
    if (gender === 1) genderPreference = 'male';
    else if (gender === 2) genderPreference = 'female';
    description = `NOTE: Client prefers ${genderPreference} workers`;
  }

  const [hasUserChangedFilter, setHasUserChangedFilter] = useState(false);

  // Logic below seems a bit odd at first glance but prevents initial mount/set of selector params loading user until the user changes the filters.
  useEffect(() => {
    if (hasUserChangedFilter) {
      dispatch(getCareWorkers(selectorParams));
    }
  }, [filterValue, dispatch, selectorParams, hasUserChangedFilter]);

  const handleFilterChange = (value: string) => {
    setFilterValue(value);
    setHasUserChangedFilter(true);
  };

  return (
    <>
      <Row className="align-items-center">
        {isReadOnly ? (
          <Col>
            <FormInput.ReadOnly
              type="readonly"
              id={id}
              name={id}
              value={appointment?.user?.full_name ?? '-'}
              link={authorised && appointment?.user?.full_name ? url : undefined}
            />
          </Col>
        ) : (
          <>
            <Col sm={8}>
              <FormInput.SelectLazy
                type="select"
                id={id}
                name={id}
                value={user_id}
                disabled={disabled}
                onChange={(name: string, value: string) => {
                  return onChange?.(name, value ?? null);
                }}
                selectorOptions={userLookup}
                initialValue={initialValue}
                selectorActions={getCareWorkers}
                selectorParams={selectorParams}
                manualReload={manualReload}
                data-cy="user-id-select"
              />
            </Col>
            <Col className="d-flex align-items-center">
              <span className="ms-3 me-2">Show</span>
              <FormInput.Select
                type="select"
                name={`filter_${id}`}
                id={`filter_${id}`}
                value={filterValue}
                clearable={false}
                onChange={(_, value) => handleFilterChange(value)}
                options={options}
                disabled={!client_id || disabled}
                ignorePlaceholder={true}
                data-cy="user-show-select"
              />
            </Col>
          </>
        )}
      </Row>
      {description.length > 0 && (
        <Row className="align-items-center">
          <Col>
            <div className={`pt-1 ps-2 pe-2 small text-${descriptionColor}`}>{description}</div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default FormInputCareWorkers;
